import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik, FormikProvider } from 'formik'
import * as yup from 'yup'
import moment from 'moment'
import { faArrowRight } from '@fortawesome/pro-light-svg-icons'
import {
  fetchEmployees,
  fetchPaymentConfigs,
  PUT_CREATE_OFF_CYCLE_PAYROLL_KEY,
  putCreateOffCyclePayroll,
} from '../../payrollActions'
import { getMinPayDateFromConfig, PAYROLL_STEPS } from '../../helpers'
import { Button, Card, Form, Link, Progress, Text, Alert } from '@/ui'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { selectActiveOnboardedEmployees } from '../../payroll.selectors'
import { TypographyStyles } from '@/styles'
import { PayrollError } from '../../PayrollError'

// TODO: Migrate to V2
import { getFieldName, makeDateSchema, makeReqStringSchema } from '@/base'

const validationSchema = yup.object({
  start_date: makeDateSchema({ field: 'start date' }),
  end_date: makeDateSchema({ field: 'end date' }).test(
    'Date Test',
    'Please enter an end date that occurs after the start date',
    function (end_date) {
      const start_date = this.parent['start_date']
      return moment(start_date, DATE_FORMATS.INPUT).isSameOrBefore(
        moment(end_date, DATE_FORMATS.INPUT)
      )
    }
  ),
  check_date: makeDateSchema({ field: 'check date', end: 'for payment' }),
  reason: makeReqStringSchema({ field: 'reason' }),
})

export const OffCyclePayroll = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const employees = useReselector(selectActiveOnboardedEmployees)
  const paymentConfig = useFetchResponse(fetchPaymentConfigs)
  const minPayDate = getMinPayDateFromConfig(paymentConfig?.payment_speed)

  const formik = useFormik({
    initialValues: {
      start_date: '',
      end_date: '',
      check_date: '',
      reason: 'Correction' as 'Correction' | 'Bonus',
    },
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true)
      const res = await putCreateOffCyclePayroll({
        start_date: moment(values.start_date, DATE_FORMATS.INPUT).format(
          DATE_FORMATS.GUSTO_SUBMIT
        ),
        end_date: moment(values.end_date, DATE_FORMATS.INPUT).format(
          DATE_FORMATS.GUSTO_SUBMIT
        ),
        check_date: moment(values.check_date, DATE_FORMATS.INPUT).format(
          DATE_FORMATS.GUSTO_SUBMIT
        ),
        off_cycle_reason: values.reason,
        employee_uuids: employees.map((emp) => emp.uuid),
        off_cycle: true,
      })(dispatch)
      setSubmitting(false)
      if (res) {
        navigate(
          `/payroll/run_payroll/process/${res.payroll_uuid}?isOffCycle=true`
        )
      }
    },
  })

  useEffect(() => {
    dispatch(fetchEmployees())
  }, [dispatch])

  return (
    <div id="run-payroll">
      <Card>
        <div className="flex flex-col gap-6">
          <Text variant={TypographyStyles.H2}>Run Off-cycle Payroll</Text>

          <Progress
            variant="segmented"
            activeSegment={1}
            numberOfSegments={PAYROLL_STEPS.length}
            segmentTitles={PAYROLL_STEPS}
          />

          <PayrollError fetchKey={PUT_CREATE_OFF_CYCLE_PAYROLL_KEY} />

          <FormikProvider value={formik}>
            <div className="flex flex-col gap-6">
              <Alert>
                Are you sure you need an off-cycle payroll? Here are some use
                cases where you should use this.{' '}
                <Link
                  href="https://support.joinheard.com/hc/en-us/articles/7256827466135"
                  newPage
                >
                  Learn here
                </Link>
              </Alert>

              <Text variant={TypographyStyles.H1}>Basics</Text>
              <Text variant={TypographyStyles.H3}>Work Period</Text>

              <Text variant={TypographyStyles.BODY_SM}>
                Enter a work period to show on your employee&apos;s pay stub.
                This is needed to accurately file your taxes.
              </Text>

              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="w-full md:w-1/5">
                  <Form.DateInput
                    required
                    name={getFieldName<typeof formik.values>('start_date')}
                  />
                </div>
                <div className="hidden md:flex md:w-1/6 justify-center w-10">
                  <Text>—</Text>
                </div>
                <div className="w-full md:w-1/5">
                  <Form.DateInput
                    required
                    name={getFieldName<typeof formik.values>('end_date')}
                  />
                </div>
              </div>

              <Form.DateInput
                required
                name={getFieldName<typeof formik.values>('check_date')}
                label="Payment Date"
                description="Enter the date you'd like your employees to receive payment."
                minDate={minPayDate.toJSDate()}
              />

              <div>
                <Form.Select
                  name={getFieldName<typeof formik.values>('reason')}
                  required
                  options={['Bonus', 'Correction'].map((option) => ({
                    text: option,
                    value: option,
                  }))}
                  label="Off-Cycle Payroll Reason"
                />
              </div>

              <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-6">
                <div className="w-full md:w-1/4">
                  <Button
                    onClick={() => navigate('/payroll/run_payroll')}
                    variant="secondary"
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
                <div className="hidden md:block md:w-1/2" />
                <div className="w-full md:w-1/4">
                  <Button
                    onClick={formik.submitForm}
                    loading={submitting}
                    iconRight={faArrowRight}
                    className="w-full"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          </FormikProvider>
        </div>
      </Card>
    </div>
  )
}
